/* eslint-disable max-lines */
/* eslint-disable max-len */
/* eslint-disable id-length */
/* eslint-disable max-lines-per-function */
import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import { TableUtility } from '@utility/dynamic-table';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import dayjs from 'dayjs';
import { useAuth } from '../../../Hooks/useAuth';
import { appendServiceNameToURL, appendServicePortToURL } from '../../../Api/resources/serviceURLs';
import { serviceName } from '../../../Api/resources/serviceConstants';
import './aiAccessReqDashBoard.scss';
import { AI_ACCESS_REQ_DASHBOARD } from '../../../Api/resources/endPoint';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import SummaryElement from './additionSummaryElement';
import { getAiAccessRequestSummary } from '../../../Api/request/aiAccessReqDashBoard';
import { MODULE_NAMES } from '../../../Constants/modules';

const PAGE_SIZE = 10;

// Build service URL once (module level, no re-runs)
const buildServiceURL = (service, path) => {
  const url = appendServiceNameToURL(service);
  if (process.env.NODE_ENV === 'production') {
    return process.env.REACT_APP_API_URL + url + path;
  }
  const port = appendServicePortToURL(service);
  const baseUrl = process.env.REACT_APP_API_URL.replace(/\/$/, '');
  return `${baseUrl}:${port}${url}${path}`;
};

const baseURL = buildServiceURL(serviceName.LANDING_MS, '/api' + AI_ACCESS_REQ_DASHBOARD,
);

const buildExportFileName = (fileName) => {
  const timestamp = dayjs().format('DD-MMM-YYYY-hh-mm-ss-A');
  return `${fileName}-${timestamp}.xlsx`;
};

const buildExportSheetName = (sheetName) => {
  return sheetName;
};

const applyToolbarButtonClasses = (rootNode) => {
  if (!rootNode) return;

  const toolbarButtons = rootNode.querySelectorAll('.button-group button');

  toolbarButtons.forEach((buttonElement) => {
    const isResetButton = Boolean(buttonElement.querySelector('.pi-refresh'));
    const isFilterButton = buttonElement.classList.contains('table-utility-filter-btn');
    const isMoreButton = buttonElement.classList.contains('ai-access-req-more-btn');
    const isExportButton = buttonElement.classList.contains('ai-access-req-export-btn');
    const isAddButton = buttonElement.classList.contains('add-button');
    const isDownloadButton = !isFilterButton && !isResetButton && !isMoreButton
    && !isExportButton && !isAddButton && Boolean(buttonElement.querySelector('svg'));

    // Map conditions directly to target class names
    const classMap = {
      'ai-access-req-dashboard-filter-btn': isFilterButton,
      'ai-access-req-dashboard-reset-btn': isResetButton,
      'ai-access-req-dashboard-export-btn': isExportButton,
      'ai-access-req-dashboard-download-btn': isDownloadButton,
      'ai-access-req-dashboard-add-btn': isAddButton,
      'ai-access-req-dashboard-more-btn': isMoreButton,
    };

    // Apply or remove classes dynamically
    Object.entries(classMap).forEach(([className, shouldApply]) => {
      buttonElement.classList.toggle(className, shouldApply);
    });
  });
};


// --- PERMISSION CHECKER ---
export const hasPermission = (moduleName, permissions, action) => {
  const actions = extractModuleActions(moduleName, permissions).map((a) =>
    String(a).toLowerCase()
      .trim(),
  );
  const targetAction = String(action).toLowerCase()
    .trim();

  switch (targetAction) {
  case 'view':
    return actions.includes('view') || actions.includes('read');
  case 'add':
  case 'create':
    return actions.includes('add') || actions.includes('create');

  case 'edit':
  case 'update':
    return actions.includes('update') || actions.includes('edit');
  case 'delete':
    return actions.includes('delete') || actions.includes('remove');

  default:
    return actions.includes(targetAction);
  }
};

const transformApiResponse = (response) => {
  // Support either full API wrapper (res.data) or the payload directly
  const payload = response?.data?.data || response?.data || response || {};

  const rawToolsData = Array.isArray(payload.toolsData) ? payload.toolsData : [];

  const usageData = rawToolsData.map((item) => ({
    // Format category to match the UI ('5+' -> '5 and More')
    tools: item.tool_count_category === '5+' ? '5 and More' : String(item.tool_count_category ?? ''),
    employees: Number(item.employee_count) || 0,
  }));

  return {
    pendingApprovals: Number(payload.pendingApprovalCount) || 0,
    pendingAccess: Number(payload.pendingAccessCount) || 0,
    usageData,
  };
};

const extractModuleActions = (moduleName, permissions) => {
  if (!permissions) return [];

  // 1. If permissions is an object map: { 'resource:ai-access-request': ['view', 'edit'] }
  if (typeof permissions === 'object' && !Array.isArray(permissions)) {
    const modulePerms = permissions[moduleName];
    if (Array.isArray(modulePerms)) return modulePerms;
    if (typeof modulePerms === 'object' && modulePerms !== null) {
      return Object.keys(modulePerms).filter((key) => Boolean(modulePerms[key]));
    }
    return [];
  }

  // 2. If permissions is an array
  if (Array.isArray(permissions)) {
    // Array of module objects: [{ module: 'resource:ai-access-request', actions: ['view'] }]
    const matchedModule = permissions.find(
      (p) => p?.moduleName === moduleName || p?.module === moduleName || p?.name === moduleName,
    );
    if (matchedModule) {
      return matchedModule.actions || matchedModule.permissions || [];
    }

    // Scoped string array: ['resource:ai-access-request:view', 'resource:ai-access-request:edit']
    const prefix = `${moduleName}:`;
    const scopedActions = permissions
      .filter((p) => typeof p === 'string' && p.startsWith(prefix))
      .map((p) => p.slice(prefix.length));
    if (scopedActions.length > 0) return scopedActions;

    // Fallback: flat action array if already scoped to the module beforehand
    return permissions;
  }

  return [];
};


const AiAccessReqDashBoard = () => {
  const { userProfile } = useAuth();
  const tableContainerRef = useRef(null);
  const tableStateRef = useRef(null);
  const moreMenuRef = useRef(null);
  const [filterPayload, setFilterPayload] = useState(null);
  const filterPayloadRef = useRef(null);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
  });
  const permissions = userProfile?.permissions || [];


  const [aiAccessRequestSummary, setAiAccessRequestSummary] = useState(null);

  // access control
  const canViewAiAccessRequestDashBoard = hasPermission(MODULE_NAMES.RESOURCE_AI_ACCESS_REQ_DASHBOARD, permissions, 'view');
  const canViewAiAccessRequestDashBoardSummary = hasPermission(MODULE_NAMES.RESOURCE_AI_ACCESS_REQ_DASHBOARD_SUMMARY, permissions, 'view');
  const canViewAiAccessRequest = hasPermission(MODULE_NAMES.RESOURCE_AI_ACCESS_REQUEST, permissions, 'view');
  const canViewAiToolMis = hasPermission(MODULE_NAMES.MIS_AI_TOOL_MIS, permissions, 'view');


  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const token = userProfile?.access_token;
        // Do not call API if token is missing or user has no summary permission
        if (!token || !canViewAiAccessRequestDashBoardSummary) return;

        const response = await getAiAccessRequestSummary({
          userInfo: userProfile,
          authToken: token,
          filterPayload: filterPayload || {},
        });

        const result = transformApiResponse(response?.data);
        setAiAccessRequestSummary(result);
      } catch (error) {
        console.error('Failed to fetch AI Access Request Summary:', error);
      }
    };

    fetchSummary();
  }, [userProfile?.authToken, userProfile?.access_token, filterPayload, canViewAiAccessRequestDashBoardSummary]);


  // Response handler — sync pagination from API response
  const handleFilteredApiResponse = useCallback((response) => {
    if (!response) return;

    const paginationData
      = response.data?.pagination
      || response.data?.paginationConfig
      || response.data?.data?.pagination
      || response.data?.data?.paginationConfig;

    const newPage = paginationData?.page ?? paginationData?.currentPage ?? 1;
    const newLimit = paginationData?.limit ?? PAGE_SIZE;
    const newTotal
      = paginationData?.total
      ?? paginationData?.count
      ?? response.meta?.total
      ?? response.data?.tableData?.length
      ?? response.data?.data?.tableData?.length
      ?? 0;

    setPagination((prev) => {
      if (prev.page === newPage && prev.limit === newLimit && prev.total === newTotal) {
        return prev;
      }
      return { page: newPage, limit: newLimit, total: newTotal };
    });
  }, []);

  // Pagination change handler
  const handlePaginationChange = useCallback((newPagination) => {
    setPagination(newPagination);
  }, []);

  const handleSetTableState = useCallback((getState) => {
    tableStateRef.current = getState;
  }, []);


  // Reset to page 1 on filter/reset actions
  const resetPaginationToFirstPage = useCallback(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  // Toolbar button class
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;
    const timer = setTimeout(() => applyToolbarButtonClasses(container), 500);
    const toolbarObserver = new MutationObserver(() => applyToolbarButtonClasses(container));
    toolbarObserver.observe(container, { childList: true, subtree: true });
    return () => {
      clearTimeout(timer);
      toolbarObserver.disconnect();
    };
  }, []);

  // eslint-disable-next-line no-unused-vars
  const handleFilterUpdate = useCallback(({ action, filters}) => {
    resetPaginationToFirstPage();

    const isResetAction
    = action === 'reset'
    || action?.type === 'reset'
    || action?.name === 'reset'
    || action === 'clear'
    || action?.type === 'clear';

    if (isResetAction) {
      setFilterPayload([]);
      filterPayloadRef.current = [];
      return;
    }

    // 1. Resolve raw filter list regardless of library naming
    const rawList = filters || [];
    const list = Array.isArray(rawList) ? rawList : [];

    // 2. Extract ONLY active fields and their selected values
    const necessaryData = list
      .filter((item) => {
        const hasValues = Array.isArray(item.selectedValues) && item.selectedValues.length > 0;
        const hasSingleValue = item.selectedValue !== undefined && item.selectedValue !== null;
        return hasValues || hasSingleValue;
      })
      .map(({ field, selectedValues, selectedValue }) => ({
        field,
        selectedValues: selectedValues?.length ? selectedValues : [selectedValue],
      }));


    // 3. Update state
    setFilterPayload(necessaryData);
    filterPayloadRef.current = necessaryData;
  }, [resetPaginationToFirstPage]);

  // 1. Navigation handlers (useCallback keeps references stable)
  const openAiAccessRequestInNewTab = useCallback(() => {
    const route = '/resource/ai-access-request';
    window.open(route, '_blank', 'noopener,noreferrer');
  }, []);

  const openAiToolMisInNewTab = useCallback(() => {
    const route = '/mis/ai-tool-mis';
    window.open(route, '_blank', 'noopener,noreferrer');
  }, []);

  // 2. Menu items with permission checks and falsy filtering
  const moreMenuItems = useMemo(
    () =>
      [
        canViewAiAccessRequest && {
          label: 'AI Access Request',
          className: 'ai-access-req-menu-item',
          command: openAiAccessRequestInNewTab,
        },
        // Include canViewAiToolMis if you have that permission defined; otherwise omit the flag
        (canViewAiToolMis) && {
          label: 'AI Tool MIS',
          className: 'ai-tool-mis-menu-item',
          command: openAiToolMisInNewTab,
        },
      ].filter(Boolean),
    [
      canViewAiAccessRequest,
      canViewAiAccessRequestDashBoard,
      canViewAiAccessRequestDashBoardSummary,
      canViewAiToolMis,
      openAiAccessRequestInNewTab,
      openAiToolMisInNewTab,
    ],
  );


  // Additional elements like more menu
  const additionalElements = useMemo(() => [
    <React.Fragment key="ai-access-req-more-menu">
      <Menu
        ref={moreMenuRef}
        model={moreMenuItems}
        popup
        appendTo={document.body}
        className="ai-access-req-more-menu"
      />
      <Button
        icon="pi pi-ellipsis-v"
        className="p-button-text ai-access-req-more-btn"
        onClick={(e) => moreMenuRef.current?.toggle(e)}
        tooltip="More options"
        tooltipOptions={{ position: 'top', className: 'ai-access-req-more-tooltip' }}
      />
    </React.Fragment>,
  ], [moreMenuItems]);

  // Static configuration
  const configuration = useMemo(
    () => ({
      generalSettings: {
        enableApiFiltering: true,
        baseURL,
        headers: { token: userProfile?.access_token || '' },
        additionalPayloadData: {
          employeeId: userProfile?.employeeId || '',
        },
        userInfo: {
          id: userProfile?.id || '',
          emailId: userProfile?.email || '',
          permissions: userProfile?.permissions || [],
          roleDetails: userProfile?.roleDetails || [],
          employeeId: userProfile?.employeeId || '',
        },
        handler: {
          handleFilteredApiResponse,
          onFilterUpdate: handleFilterUpdate,
          setTableStateHandler: handleSetTableState,
        },
        apiEndpoints: {
          getAllViews: { endpoint: '', enabled: false },
          // Disable table data fetch if user only has summary permission
          getFilteredData: {
            endpoint: '',
            enabled: true,
          },
          saveOrUpdateView: { endpoint: '', enabled: false },
          deleteView: { endpoint: '', enabled: false },
          download: {
            endpoint: 'export',
            enabled: Boolean(canViewAiAccessRequestDashBoard),
            filename: () => buildExportFileName('AI-Access-Request-Dashboard'),
            sheetName: () => buildExportSheetName('AI-Access-Request-Dashboard'),
          },
        },
        showToolBar: true,
        showPagination: false,
        toolBarSettings: {
          showFilterOption: true, // Keep filters visible
          showExpandCollapse: false,
          showDownloadExcel: Boolean(canViewAiAccessRequestDashBoard), // Hide excel export if no table
          downloadExcelSheetName: 'AI Access Request',
          showColumnCustomize: false,
          showHelp: false,
          showSearch: false,
          showSaveView: false,
          showToolBarHeading: true,
          toolBarHeading: canViewAiAccessRequestDashBoard ? 'AI Usage Dashboard' : 'AI Access Summary',
          showReset: false,
          additionalElements: additionalElements,
        },
      },
      showFilterOption: true,
      tableSettings: {
        enableTreeTable: false,
        showExpandCollapseChildrens: true,
        expandCollapseChildrensConfig: { expandIcon: '+', collapseIcon: '-' },
        size: 'large',
        loaderSkeleton: { rows: 10, bodyCells: 1, columns: 8 },
        pageSize: PAGE_SIZE,
        paginationConfig: {
          ...pagination,
          onPaginationChange: handlePaginationChange,
          showPageSizeSelector: false,
        },
        additionalBodyElements: canViewAiAccessRequestDashBoardSummary && (
          <SummaryElement
            pendingApprovals={aiAccessRequestSummary?.pendingApprovals}
            pendingAccess={aiAccessRequestSummary?.pendingAccess}
            usageData={aiAccessRequestSummary?.usageData}
          />
        ),
        showPageSizeSelector: false,
        maxBreakdowns: 5,
        showExpandCollapse: false,
        tableScrollHeight: 'max-content',
        maxFrozenColumns: { desktop: 4, tablet: 2, mobile: 1 },
      },
    }),
    [
      userProfile,
      pagination,
      handleFilteredApiResponse,
      handlePaginationChange,
      handleSetTableState,
      handleFilterUpdate,
      additionalElements,
      aiAccessRequestSummary,
      canViewAiAccessRequestDashBoard,
      canViewAiAccessRequestDashBoardSummary,
    ],
  );

  if (!canViewAiAccessRequestDashBoard && !canViewAiAccessRequestDashBoardSummary) {
    return <div>You do not have permission to view AI access request dashboard.</div>;
  }

  return (
    <div className="ai-access-req-dashboard-outer">
      <div
        className={`ai-access-req-dashboard-table ${
          !canViewAiAccessRequestDashBoard ? 'only-summary-view' : ''
        }`}
      >
        <div className="ai-access-req-dashboard-table-utility" ref={tableContainerRef}>
          <TableUtility configuration={configuration} />
        </div>
      </div>
    </div>
  );
};
export default AiAccessReqDashBoard;
